import * as React from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import { HeaderCell, Table, TableBody, TableHead } from '../table';
import { DetailTable, DetailTableCell } from './models';
import clsx from 'clsx';
import { DateFormatter, DateTimeFormatter } from '../shared/date-format';
import TextStyles from '../theme/text';
import { blue } from '../theme/colors';
import { Link } from 'react-router-dom';

export interface DetailTableProps {
  detailTable: DetailTable;
  columnCount: number;
  collapsed: boolean;
  nestLevel: number;
}

interface StylesProps {
  tableHeight: number;
  nestLevel: number;
  columnCount: number;
}

const useStyles = makeStyles<Theme, StylesProps>({
  detailTable: {
    paddingLeft: ({ nestLevel }) => nestLevel * 30 + 30,
    display: 'flex',
    margin: '8px 0 16px',
    zIndex: 0,
    gridColumn: ({ columnCount }) => `1 / ${columnCount + 1}`,
    height: ({ tableHeight }) => tableHeight,
    transition: 'height 300ms',
    '& td, & th': {
      padding: '0 3px'
    },
    '&.collapsed': {
      height: 0,
      margin: 0
    }
  },
  link: {
    ...TextStyles.caption,
    color: blue[600],
    textDecoration: 'none'
  }
});

const DataGridDetailTable = ({
  detailTable,
  collapsed,
  nestLevel,
  columnCount
}: DetailTableProps) => {
  const tableHeight = 22 + detailTable.rows.length * 16;
  const classes = useStyles({ tableHeight, nestLevel, columnCount });

  const renderCellContent = (cell: DetailTableCell) => {
    let content = cell.content;

    if (cell.format) {
      if (cell.format == 'date') {
        content = DateFormatter.format(new Date(cell.content));
      } else if (cell.format == 'datetime') {
        content = DateTimeFormatter.format(new Date(cell.content));
      }
    }

    if (cell.isLink && cell.link) {
      if (cell.link.isExternal) {
        return (
          <a
            href={cell.link.url}
            className={classes.link}
            rel="nofollow noreferrer"
            target="_blank"
          >
            {content}
          </a>
        );
      } else {
        <Link to={cell.link.url}>{content}</Link>;
      }
    }

    return content;
  };

  return (
    <div className={clsx(classes.detailTable, collapsed && 'collapsed')}>
      <Table>
        <TableHead>
          {detailTable.columns.map((column, i) => (
            <HeaderCell title={column} key={i} />
          ))}
        </TableHead>
        <TableBody>
          {detailTable.rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={`dt-cell-${j}`}>{renderCellContent(cell)}</td>
              ))}
            </tr>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default DataGridDetailTable;
